import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return localStorage.getItem("isAuthenticated") === "true";
    });
    const navigate = useNavigate();
    // eslint-disable-next-line
    const location = useLocation();

    // Session Timeout Logic (4 Hours)
    // 4 hours * 60 minutes * 60 seconds * 1000 ms
    const SESSION_DURATION = 4 * 60 * 60 * 1000;

    const checkSession = () => {
        const loginTime = localStorage.getItem("loginTime");
        if (loginTime) {
            const now = Date.now();
            if (now - parseInt(loginTime, 10) > SESSION_DURATION) {
                console.log("Session expired. Logging out.");
                logout();
            }
        }
    };

    useEffect(() => {
        // Check on mount
        checkSession();

        // Check every minute
        const interval = setInterval(() => {
            checkSession();
        }, 60000); // 1 minute check

        return () => clearInterval(interval);
    }, []);

    const login = async (email, password) => {
        try {
            // Send both email and emil (backend quirk compatibility)
            const result = await api.login({ email, password });

            if (result && (result.success || result.message === "Login successful")) {
                // Handle nested data object if present (api fix)
                const data = result.data || result;

                setIsAuthenticated(true);
                localStorage.setItem("isAuthenticated", "true");
                localStorage.setItem("loginTime", Date.now()); // Set session start time

                if (data.token) {
                    localStorage.setItem("auth_token", data.token);
                }
                if (data.comp_code) {
                    localStorage.setItem("comp_code", data.comp_code);
                }

                // Helper to find company name deep in the object
                const findCompanyName = (obj) => {
                    if (!obj) return null;
                    // Direct check (Priority based on user feedback)
                    if (obj.companyname) return obj.companyname;
                    if (obj.companyName) return obj.companyName;
                    if (obj.company_name) return obj.company_name;
                    if (obj.CompanyName) return obj.CompanyName;

                    // Nested check in common sub-objects
                    if (obj.company) {
                        if (obj.company.companyname) return obj.company.companyname;
                        if (obj.company.name) return obj.company.name;
                        if (obj.company.companyName) return obj.company.companyName;
                    }
                    if (obj.user) {
                        if (obj.user.companyname) return obj.user.companyname;
                        if (obj.user.companyName) return obj.user.companyName;
                    }



                    return null;
                };

                const nameFromApi = findCompanyName(data) || findCompanyName(result);

                if (nameFromApi) {
                    localStorage.setItem("company_name", nameFromApi);
                } else {
                    console.warn("Could not find Company Name in login response:", result);
                }

                // Helper to find user role
                const findUserRole = (obj) => {
                    if (!obj) return null;
                    if (obj.role) return obj.role;
                    if (obj.userRole) return obj.userRole;
                    if (obj.isAdmin !== undefined) return obj.isAdmin ? "Admin" : "User";

                    if (obj.user) {
                        if (obj.user.role) return obj.user.role;
                        if (obj.user.isAdmin !== undefined) return obj.user.isAdmin ? "Admin" : "User";
                    }
                    return null;
                };

                const roleFromApi = findUserRole(data) || findUserRole(result);

                if (roleFromApi) {
                    localStorage.setItem("user_role", roleFromApi);
                } else {
                    // Fallback: If no role is found, but this is the initial login after registration or implied, 
                    // we might default to 'Admin' or handle it. 
                    // For now, if undefined, we might assume 'User' or 'Admin' based on context?
                    // Safe default: Store nothing and handle in UI (e.g., default to Admin if vague? Or strict?)
                    // Let's assume 'Admin' for now if we can't find it to avoid locking out the owner, 
                    // UNLESS the prompt implies multi-role system is active.
                    // Actually, getting 'Admin' privileges usually requires explicit data.
                    // But to be safe for the "Demo", I'll set it to 'Admin' if the email matches the company creator? 
                    // I don't know the creator.
                    // I'll log it and NOT set it, effectively making it null.
                }

                // Store user email for validation purposes
                localStorage.setItem("user_email", email);

                navigate("/dashboard");
                return { success: true };
            }
            return { success: false, message: result.message || "Login failed" };
        } catch (error) {
            console.error("Login error:", error);
            return { success: false, message: "Network error" };
        }
    };

    const register = async (companyName, email, password) => {
        try {
            const result = await api.registerCompany({ companyName, emil: email, password });
            if (result && result.success) {
                return { success: true };
            }
            return { success: false, message: result.message || "Registration failed" };
        } catch (error) {
            console.error("Registration error:", error);
            return { success: false, message: "Network error" };
        }
    };

    const logout = () => {
        setIsAuthenticated(false);
        localStorage.removeItem("isAuthenticated");
        localStorage.removeItem("auth_token");
        localStorage.removeItem("comp_code");
        localStorage.removeItem("company_name");
        localStorage.removeItem("company_name");
        localStorage.removeItem("user_email");
        localStorage.removeItem("loginTime"); // Clear session time
        navigate("/authentication/sign-in");
    };



    return <AuthContext.Provider value={{ isAuthenticated, login, register, logout }}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
